// Plain-language summaries of triggers and steps for the scenario list and the flow view (R-U11: no technical words).

import type { MetaIndex, Step, Trigger } from '@confdeck/core-shared';

const COMMAND_NAMES: Record<string, string> = {
  'record.create': 'создана запись', 'record.update': 'изменена запись', 'record.archive': 'запись в архиве', 'record.restore': 'запись вернули из архива', 'record.import': 'импорт',
};

export function describeTrigger(t: Trigger, meta: MetaIndex): string {
  const type = 'typeId' in t && t.typeId ? meta.type(t.typeId)?.nameMany ?? 'раздел' : '';
  switch (t.type) {
    case 'record.created': return `Новая запись в «${type}»`;
    case 'record.updated': {
      const f = meta.field(t.typeId, t.field);
      // Stages of different pipelines share names («Выиграна»): name the pipeline when there are several.
      const terms = (t.to ?? []).map((v) => meta.term(String(v)));
      const parents = new Set(terms.map((x) => x?.parentId));
      const to = terms.map((x) => (x ? (parents.size > 1 && x.parentId ? `${meta.term(x.parentId)?.name}: ${x.name}` : x.name) : '?'));
      return `«${type}»: меняется «${f?.name ?? t.field}»${to.length ? ` на ${to.map((x) => `«${x}»`).join(' или ')}` : ''}`;
    }
    case 'command': return `После действия: ${COMMAND_NAMES[t.command] ?? t.command}${type ? ` в «${type}»` : ''}`;
    case 'date': {
      const f = meta.field(t.typeId, t.field);
      const d = t.offsetDays;
      return `«${type}»: ${d === 0 ? 'в день' : d < 0 ? `за ${-d} дн. до` : `через ${d} дн. после`} даты «${f?.name ?? t.field}»`;
    }
    case 'form.submitted': return `Новый ответ формы «${meta.form(t.formId)?.name ?? 'форма удалена'}»`;
  }
}

export function describeStep(s: Step, meta: MetaIndex, typeId?: string, templates?: Map<string, string>): string {
  switch (s.type) {
    case 'setField': return `Изменить «${typeId ? meta.field(typeId, s.field)?.name ?? s.field : s.field}»`;
    case 'createRecord': return `Создать: ${meta.type(s.typeId)?.name.toLowerCase() ?? 'запись'}${s.linkTo ? ', связать с записью сценария' : ''}`;
    case 'command': return `Действие: ${COMMAND_NAMES[s.command] ?? s.command}`;
    case 'sendMail': {
      // People see the name of the template; the code stays the machine's word for it.
      const tpl = templates?.get(s.template) ?? s.template;
      const to = s.toField ? (typeId ? meta.field(typeId, s.toField)?.name ?? s.toField : s.toField) : prettyTemplate(s.to, meta, typeId);
      return `Отправить письмо «${tpl}» → ${to}`;
    }
    case 'webhook': { let host = s.url; try { host = new URL(s.url).host; } catch { /* shown as typed */ } return `Вебхук → ${host || 'адрес не указан'}`; }
    case 'wait': {
      // Whole days and whole hours read short; anything else is told exactly, not rounded (Grok review 30.10, finding 19).
      if (s.minutes % 1440 === 0) return `Подождать ${s.minutes / 1440} дн.`;
      const h = Math.floor(s.minutes / 60), m = s.minutes % 60;
      if (!h) return `Подождать ${m} мин`;
      return m ? `Подождать ${h} ч ${m} мин` : `Подождать ${h} ч`;
    }
    case 'branch': return 'Если условие выполнено';
    case 'stop': return `Остановить: ${s.reason}`;
  }
}

/** «{{record.data.name}}» → «‹Название›» for display of templates. */
export function prettyTemplate(v: unknown, meta: MetaIndex, typeId?: string): string {
  if (v == null) return '—';
  if (Array.isArray(v)) return v.map((x) => prettyTemplate(x, meta, typeId)).join(', ');
  const s = typeof v === 'string' ? v : JSON.stringify(v);
  return s.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_, e: string) => {
    if (e === 'record.id') return '‹эта запись›';
    if (e === 'record.ownerId') return '‹ответственный›';
    const today = e.match(/^today([+-]\d+)?$/);
    if (today) return today[1] ? `‹сегодня ${today[1]} дн.›` : '‹сегодня›';
    if (e.startsWith('record.data.')) { const k = e.slice(12); return `‹${(typeId && meta.field(typeId, k)?.name) || k}›`; }
    return `‹${e}›`;
  }).replace(/^[0-9a-f-]{36}$/, (id) => meta.term(id)?.name ?? meta.user(id)?.name ?? id);
}
